import { CptySettlementTypeEnum, logger, SecurityTypesUtils, SizeModeEnum, toBig } from '@talos/kyoko';
import { parse } from 'date-fns';
import { entries } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { UNITS_CURRENCY } from '../../../../../components/OMS/CareOrderFormView/types';
import { convertDateToDefaultCareOrderUTCDate } from '../../../../../components/OMS/CareOrderFormView/utils';
import { type OrgConfigurationContextProps, OrgConfigurationKey } from '../../../../../providers';
import { EMPTY_IMPORT_CARE_ORDER_ACTION, type ImportedCareOrder } from '../types';
import { getImportedCareOrdersWithActions } from '../utils';
import type { CareOrderImportContext } from './types';

export interface UnqorkOrder {
  id: string;
  orderNumber: string;
  tradeDate: string;
  settlementDate: string;
  totalShares: number;
  orderType: string;
  settlementType: string | null;
  basketSettlementType?: string | null;
  authorizedParticipant: string;
  liquidityProvider: string | null; // <-- have seen this be null, valid
  baskets: number;
  totalAssets: number;
  status: string;
  productId: string;
  assetId: string;
}

interface UnqorkProduct {
  /** The Unqork ProductID */
  ProductID: string;
  /** The Talos Basket.BasketSymbol */
  BasketSymbol: string;
}

interface UnqorkAuthorizedParticipant {
  /** The Unqork AP value */
  AuthorizedParticipant: string;
  /** The talos Counterparty.Name */
  Counterparty: string;
}

export function unqorkOrdersToImportedCareOrders(
  unqorkOrders: UnqorkOrder[],
  context: CareOrderImportContext
): ImportedCareOrder[] {
  const { baskets, fundDefinitions, allCareOrders } = context;

  const apMap = getAuthorizedParticipantMapping(context.getConfig);
  const productMap = getProductIDMapping(context.getConfig);
  const useFundDefs = context.getConfig(OrgConfigurationKey.UseFundDefinitionAndTradableIndexesStreams, false);

  const importedCareOrders: ImportedCareOrder[] = [];
  for (const unqorkOrder of unqorkOrders) {
    const basketSymbol = productMap?.get(unqorkOrder.productId)?.BasketSymbol;
    if (!basketSymbol) {
      throw new Error(`Unable to find Product ID ${unqorkOrder.productId} in the Product ID map.`);
    }

    const fundDefinition = fundDefinitions.find(fund => fund.Symbol === basketSymbol);
    let compoundSymbol: string | undefined;
    if (useFundDefs) {
      if (!fundDefinition) {
        throw new Error(`Unable to resolve a FundDefinition from this order. Product ID ${unqorkOrder.productId}.`);
      }
      compoundSymbol = getFirstCompoundTradableSecuritySymbol(fundDefinition, context);
      if (!compoundSymbol) {
        throw new Error(
          `Unable to resolve a compound tradable security from FundDefinition ${fundDefinition.Symbol}. Product ID ${unqorkOrder.productId}.`
        );
      }
    } else {
      const basket = baskets?.find(b => b.BasketSymbol === basketSymbol);
      if (!basket) {
        throw new Error(`Unable to resolve a Basket from this order. Product ID ${unqorkOrder.productId}.`);
      }
    }

    const counterpartyName = apMap?.get(unqorkOrder.authorizedParticipant)?.Counterparty;
    if (unqorkOrder.authorizedParticipant && !counterpartyName) {
      throw new Error(
        `Found an AP property on the unqork order, but was not able to resolve any Talos counterparty. AP: ${unqorkOrder.authorizedParticipant}.`
      );
    }

    const unitQty = toBig(unqorkOrder.totalShares)?.div(10_000);
    if (!unitQty) {
      throw new Error(`Was not able to grab any totalShares. Raw totalShares: ${unqorkOrder.totalShares}.`);
    }

    const settleDate = unqorkOrderSettleDateToISOSettleDate(unqorkOrder.settlementDate);
    if (!settleDate) {
      throw new Error(`Was not able to parse any settle date. Raw settle date: ${unqorkOrder.settlementDate}.`);
    }

    importedCareOrders.push({
      ClOrdID: uuid(),
      Side: unqorkOrder.orderType === 'Creation' ? 'Buy' : 'Sell',
      Group: unqorkOrder.orderNumber ?? undefined,
      Counterparty: counterpartyName,
      SettleDate: settleDate,
      CptySettlementType: parseUnqorkSettleType(unqorkOrder),
      Annotations: {
        ETP: {
          InitiatingFrom: unqorkOrder.liquidityProvider ?? undefined, // normalize null to undefined
        },
        BasketOrder: {
          BasketSymbol: basketSymbol,
          BasketOrderQty: useFundDefs ? undefined : unitQty.toFixed(),
        },
      },

      OrderQty: unitQty.toFixed(),
      units: unitQty.toFixed(),
      Currency: UNITS_CURRENCY,
      Symbol: compoundSymbol, // compoundSymbol is only defined and relevant for the useFundDefs case
      SizeMode: useFundDefs ? SizeModeEnum.Units : undefined,

      action: EMPTY_IMPORT_CARE_ORDER_ACTION,
    });
  }

  return getImportedCareOrdersWithActions(importedCareOrders, allCareOrders, 'unqork');
}

function parseUnqorkSettleType(order: UnqorkOrder): CptySettlementTypeEnum | undefined {
  if ('basketSettlementType' in order) {
    return parseUnqorkBasketSettleType(order.basketSettlementType);
  }

  return parseLegacyUnqorkSettleType(order.settlementType);
}

function parseUnqorkBasketSettleType(value: string | null | undefined): CptySettlementTypeEnum {
  if (value == null) {
    return CptySettlementTypeEnum.Cash;
  }

  switch (value) {
    case 'Cash':
      return CptySettlementTypeEnum.Cash;
    case 'In-Kind':
      return CptySettlementTypeEnum.InKind;
  }

  throw new Error(`Unsupported basketSettlementType: ${value}.`);
}

// We can remove this legacy parsing in a few months. This is just here for backwards compat as of 13th Aug 2026.
function parseLegacyUnqorkSettleType(value: string | null | undefined): CptySettlementTypeEnum | undefined {
  if (value == null) {
    return undefined;
  }

  switch (value) {
    case 'Cash In Lieu':
      return CptySettlementTypeEnum.Cash;
    case 'Inkind':
      return CptySettlementTypeEnum.InKind;
  }

  throw new Error(`Unsupported settlementType: ${value}.`);
}

function getFirstCompoundTradableSecuritySymbol(
  fundDefinition: CareOrderImportContext['fundDefinitions'][number],
  context: CareOrderImportContext
): string | undefined {
  return fundDefinition.TradableSecurities?.find(tradableSymbol =>
    SecurityTypesUtils.Compound.isType(context.securitiesBySymbol.get(tradableSymbol))
  );
}

export function unqorkOrderSettleDateToISOSettleDate(dateString: string): string | undefined {
  try {
    const localDate = parse(dateString, 'yyyy-MM-dd', new Date());
    const standardizedDate = convertDateToDefaultCareOrderUTCDate(localDate);
    const isoString = standardizedDate.toISOString();
    return isoString;
  } catch (e) {
    return undefined;
  }
}

function getProductIDMapping(
  getConfig: OrgConfigurationContextProps['getConfig']
): Map<string, UnqorkProduct> | undefined {
  const stringConfig = getConfig(OrgConfigurationKey.UnqorkProductIDMap, '');
  if (stringConfig === '') {
    return undefined;
  }

  try {
    const config: Record<string, UnqorkProduct> = JSON.parse(stringConfig);
    return new Map(entries(config));
  } catch (e) {
    logger.error(new Error('Failed to parse rfq.UnqorkProductIDMap'), { extra: { stringConfig } });
    return undefined;
  }
}

function getAuthorizedParticipantMapping(
  getConfig: OrgConfigurationContextProps['getConfig']
): Map<string, UnqorkAuthorizedParticipant> | undefined {
  const stringConfig = getConfig(OrgConfigurationKey.UnqorkAuthorizedParticipantMap, '');
  if (stringConfig === '') {
    return undefined;
  }

  try {
    const config: Record<string, UnqorkAuthorizedParticipant> = JSON.parse(stringConfig);
    return new Map(entries(config));
  } catch (e) {
    logger.error(new Error('Failed to parse rfq.UnqorkAuthorizedParticipantMap'), { extra: { stringConfig } });
    return undefined;
  }
}
